// Business dates are Melbourne-local, not UTC. A bare
// new Date().toISOString().slice(0, 10) is the previous calendar day for
// ~10-11 hours every evening AEST/AEDT, which skews "due today" / overdue
// flags and auto-stamped paid/delivered dates. Always derive YYYY-MM-DD
// through here.

// en-CA formats as YYYY-MM-DD.
const melbourne = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Australia/Melbourne',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Today's date (YYYY-MM-DD) in Australia/Melbourne. */
export function melbourneToday(): string {
  return melbourne.format(new Date());
}

/** A timestamp as a Melbourne-local YYYY-MM-DD. Use this when elapsed UI or
    automation state starts from an INTEGER-ms database column. */
export function melbourneDate(at: number | Date): string {
  return melbourne.format(at instanceof Date ? at : new Date(at));
}

const melbourneTimeFormatter = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne', hour: '2-digit', minute: '2-digit', hour12: false,
});

/** A timestamp as a Melbourne-local 24-hour HH:MM (including midnight 00:00). */
export function melbourneTime(at: number | Date): string {
  return melbourneTimeFormatter.format(at instanceof Date ? at : new Date(at));
}

/** True only for a real Gregorian calendar date in canonical YYYY-MM-DD
    form. Date.UTC normalises impossible dates (for example Feb 31) instead
    of rejecting them, so compare every UTC component back to the input. */
export function isValidCalendarDate(dateStr: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

/** Whole calendar-day distance between ISO business dates. UTC calendar
    arithmetic avoids host-timezone and DST edges because the inputs are
    dates, not instants. */
export function calendarDaysBetween(fromIso: string, toIso: string): number {
  const [fy, fm, fd] = fromIso.split('-').map(Number);
  const [ty, tm, td] = toIso.split('-').map(Number);
  return Math.floor((Date.UTC(ty!, tm! - 1, td!) - Date.UTC(fy!, fm! - 1, fd!)) / 86_400_000);
}

/** Add calendar days without DST or host-timezone shifts. Input is an ISO business date. */
export function addCalendarDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day! + days));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

/** Weekday of a business date: Sunday = 0 through Saturday = 6. */
export function calendarWeekday(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getUTCDay();
}

/** ISO week-year (which can differ from the calendar year near New Year). */
export function isoWeekKey(iso: string): string {
  const thursday = addCalendarDays(iso, 3 - (calendarWeekday(iso) + 6) % 7);
  const year = thursday.slice(0, 4);
  const week = Math.floor(calendarDaysBetween(`${year}-01-01`, thursday) / 7) + 1;
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/** Epoch ms at Melbourne midnight, for timestamp queries over business dates.
 * Resolve the zone offset at the resulting instant too: a DST transition can
 * occur between UTC midnight and Melbourne midnight on the same date. */
export function melbourneStartOfDay(iso: string): number {
  const target = Date.parse(`${iso}T00:00:00Z`);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Melbourne', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });
  let instant = target;
  for (let i = 0; i < 3; i++) {
    const parts = Object.fromEntries(formatter.formatToParts(instant).map(p => [p.type, p.value]));
    const localAsUtc = Date.parse(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}Z`);
    instant += target - localAsUtc;
  }
  return instant;
}

/** Whole inclusive-day overlap test for two YYYY-MM-DD ranges (project crew
    double-booking warnings, migration 0031). ISO dates sort lexicographically
    so plain string comparison is safe. Two ranges overlap iff each one starts
    on or before the other ends; adjacent ranges (one ends the day before the
    other starts, with no shared day) do NOT overlap. */
export function datesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

/** Mon–Sun week window containing the given ISO date (inclusive bounds).
    Pure calendar maths over YYYY-MM-DD — the caller supplies "today" from
    melbourneToday() so the window is the Melbourne business week. */
export function weekOf(iso: string): { start: string; end: string } {
  const d = new Date(`${iso}T00:00:00`);
  const sinceMonday = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  const fmt = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  const start = new Date(d);
  start.setDate(d.getDate() - sinceMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start: fmt(start), end: fmt(end) };
}

/** Short clock readout for cached snapshots, always Melbourne time. */
export function melbourneClock(at: number): string {
  return new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Melbourne', hour: 'numeric', minute: '2-digit', hour12: true })
    .format(new Date(at)).replace(/\s/g, '').toLowerCase();
}

/** Display a YYYY-MM business period without converting it to a timestamp. */
export function calendarMonthName(key: string, short = false): string {
  const name = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][Number(key.slice(5, 7)) - 1];
  return name ? short ? name.slice(0, 3) : name : key;
}

export function previousMonthKey(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
}
